<template>
  <v-container>
    <v-card class="mx-auto rounded-xl">
      <v-row justify="center">
        <v-col cols="12">
          <v-card class="pa-4" elevation="4">
            <v-card-title>
              {{ isEditing ? 'Editar Empresa' : 'Novo Empresa' }}
            </v-card-title>
            <v-card-text>

              <v-tabs v-model="currentTab" dark class="mb-18">
                <v-tab value="details">Detalhes</v-tab>
              </v-tabs>

              <v-form ref="formRef" @submit.prevent="handleSubmit">
                <v-window v-model="currentTab" class="mt-2">
                  <v-window-item value="details">
                    <v-row class="mt-2">
                      <v-col cols="3">
                        <v-text-field v-model="formValue.document" label="CNPJ"
                          :rules="[rules.document]"></v-text-field>
                      </v-col>
                      <v-col cols="4">
                        <v-text-field v-model="formValue.name" label="Razão Social" :rules="[rules.name]"></v-text-field>
                      </v-col>
                      <v-col cols="3">
                        <v-text-field v-model="formValue.phone" label="Telefone de exibição"
                          ></v-text-field>
                      </v-col>
                      <v-col cols="3">
                        <v-text-field v-model="formValue.whatsapp" label="Whatsapp" v-maska="'(##) #####-####'"
                          :rules="[rules.phone]"></v-text-field>
                      </v-col>
                      <v-col cols="2">
                        <v-text-field v-model="formValue.creci" label="CRECI" :rules="[rules.creci]"></v-text-field>
                      </v-col>
                      
                      <v-col cols="2">
                        <v-text-field v-model="formValue.zip_code" label="CEP"
                          :rules="[rules.zip_code]"></v-text-field>
                      </v-col>
                      <v-col cols="8">
                        <v-text-field v-model="formValue.address" label="Logradouro"></v-text-field>
                      </v-col>
                      <v-col cols="2">
                        <v-text-field v-model="formValue.number" label="Número"></v-text-field>
                      </v-col>
                      <v-col cols="5">
                        <v-text-field v-model="formValue.neighborhood" label="Bairro"></v-text-field>
                      </v-col>
                      <v-col cols="6">
                        <v-text-field v-model="formValue.city" label="Cidade"></v-text-field>
                      </v-col>
                      <v-col cols="1">
                        <v-text-field v-model="formValue.state" label="UF"></v-text-field>
                      </v-col>
                      <v-col cols="6">
                        <v-text-field v-model="formValue.email" label="E-mail" :rules="[rules.email]"></v-text-field>
                      </v-col>

                      <v-col cols="12" sm="6" md="3">
                        <v-switch v-model="formValue.is_active" color="success" label="Empresa Ativa?"></v-switch>
                      </v-col>
                    </v-row>
                  </v-window-item>
                </v-window>
              </v-form>
            </v-card-text>

            <v-card-actions class="d-flex justify-space-between">
              <v-btn color="secondary" variant="flat" @click="handleCancel">
                <v-icon left>mdi-arrow-left</v-icon>
                Voltar
              </v-btn>
              <v-btn color="primary" variant="flat" :loading="isSubmitting" :disabled="isSubmitting"
                @click="handleSubmit">
                <v-icon left>mdi-content-save</v-icon>
                {{ isEditing ? 'Salvar Alterações' : 'Cadastrar Empresa' }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-col>
      </v-row>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { vMaska } from "maska/vue";
import api from '@/services/api';
import { useToastStore } from '@/store/toast';
import { useLoadingOverlay } from '@/store/loadingOverlay';

const toastStore = useToastStore();
const loadingOverlay = useLoadingOverlay();
const route = useRoute();
const router = useRouter();
const formRef = ref(null);

const currentTab = ref('details');
const isSubmitting = ref(false);

const formValue = ref({
  name: '',
  document: '',
  creci: '',
  phone: '',
  whatsapp: '',
  email: '',
  zip_code: '',
  address: '',
  number: '',
  neighborhood: '',
  city: '',
  state: '',
  is_active: true,
});

const isEditing = computed(() => !!route.params.id);

const rules = {
  name: v => !!v || 'O nome é obrigatório.',
  email: v => !!v || 'O e-mail é obrigatório.',
  whatsapp: v => !!v || 'O Whatsapp é obrigatório.',
  creci: v => !!v || 'O CRECI é obrigatório.',
  document: v => !!v || 'O CNPJ é obrigatório.',
};

const loadEnterprise = async (id) => {
  loadingOverlay.startLoading();
  try {
    const response = await api.get(`/enterprises/${id}`);
    const data = response.data.data;

    if (data) {
      formValue.value = {
        document: data.document,
        creci: data.creci,
        name: data.name,
        phone: data.phone,
        whatsapp: data.whatsapp,
        email: data.email,
        zip_code: data.zip_code,
        address: data.address,
        number: data.number,
        neighborhood: data.neighborhood,
        city: data.city,
        state: data.state,
        is_active: data.is_active === 1,
      };
    } else {
      toastStore.showToast({ message: 'A empresa não foi encontrada.', color: 'warning' });
    }

  } catch (error) {
    console.error('Erro ao carregar os dados da empresa:', error);
    toastStore.showToast({
      message: error?.response?.data?.error || 'Erro ao carregar os dados da empresa',
      color: 'error',
    });
  } finally {
    loadingOverlay.stopLoading();
  }
};

const handleSubmit = async () => {
  const { valid } = await formRef.value.validate();
  if (!valid) {
    currentTab.value = 'details';
    return;
  }

  isSubmitting.value = true;

  // Monta payload simples
  const payload = { ...formValue.value };
  
  // Converte boolean para 1/0
  payload.is_active = payload.is_active ? 1 : 0;

  try {
    if (isEditing.value) {
      await api.put(`/enterprises/${route.params.id}`, payload);
    } else {
      await api.post('/enterprises', payload);
    }

    router.push('/app/empresa');
    toastStore.showToast({
      message: isEditing.value ? 'Empresa atualizada com sucesso!' : 'Empresa cadastrada com sucesso!',
      color: 'success',
    });

  } catch (error) {
    console.error('Erro ao salvar empresa:', error);
    toastStore.showToast({
      message: error?.response?.data?.error || 'Erro ao salvar empresa',
      color: 'error',
    });
  } finally {
    isSubmitting.value = false;
  }
};

const handleCancel = () => {
  router.push('/app/empresa');
};

onMounted(() => {
  if (isEditing.value) {
    loadEnterprise(route.params.id);
  }
});
</script>
